import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../ai.scss';
import Header from 'shared/components/header/header';
import { firstLevelBreadcrumbs } from 'shared/constants/constants';
import { PermissionList } from 'shared/interface/enum';
import { checkPermission } from 'shared/util/permission';
import ConversationListPanel from '../component/conversationListPanel';
import ChatPanel from '../component/chatPanel';
import { AI_URL_PARAM_CONVERSATION_ID, AI_URL_PARAM_MESSAGE_ID, AiConversation } from '../interface/ai';
import { fetchConversation } from '../util/aiActions';

const AiAssistant: React.FC = () => {
	const canList = checkPermission({
		anyOnePermission: [PermissionList.AiConversationList, PermissionList.FullAccess]
	});
	const canGet = checkPermission({
		anyOnePermission: [PermissionList.AiConversationGet, PermissionList.FullAccess]
	});
	const canAsk = checkPermission({
		anyOnePermission: [PermissionList.AiConversationAsk, PermissionList.FullAccess]
	});

	const [searchParams, setSearchParams] = useSearchParams();
	const urlConversationId = searchParams.get(AI_URL_PARAM_CONVERSATION_ID)?.trim() || '';
	const targetMessageId = searchParams.get(AI_URL_PARAM_MESSAGE_ID)?.trim() || '';

	const [conversations, setConversations] = useState<AiConversation[]>([]);
	const [selectedConversation, setSelectedConversation] = useState<AiConversation | null>(null);
	const [isNewChat, setIsNewChat] = useState(() => canAsk && !canList && !urlConversationId);
	const [listRefreshToken, setListRefreshToken] = useState(0);
	const urlLoadRequestIdRef = useRef(0);
	const urlConversationInFlightRef = useRef('');
	const urlConversationFailedRef = useRef('');
	const conversationsRef = useRef(conversations);
	conversationsRef.current = conversations;

	const syncUrlWithSelection = useCallback(
		(conversation: AiConversation | null, messageId?: string | null) => {
			if (!conversation) {
				setSearchParams({});
				return;
			}

			const next = new URLSearchParams();
			next.set(AI_URL_PARAM_CONVERSATION_ID, conversation.conversationId);
			if (messageId) {
				next.set(AI_URL_PARAM_MESSAGE_ID, messageId);
			}
			setSearchParams(next);
		},
		[setSearchParams]
	);

	const handleSelectConversation = useCallback(
		(conversation: AiConversation | null) => {
			if (!conversation) {
				setSelectedConversation(null);
				setIsNewChat(true);
				syncUrlWithSelection(null);
				return;
			}

			setSelectedConversation(conversation);
			setIsNewChat(false);
			syncUrlWithSelection(conversation);
		},
		[syncUrlWithSelection]
	);

	const handleConversationCreated = useCallback(
		(conversation: AiConversation) => {
			setConversations((prev) => {
				const exists = prev.some((item) => item.conversationId === conversation.conversationId);
				if (exists) {
					return prev.map((item) =>
						item.conversationId === conversation.conversationId ? conversation : item
					);
				}
				return [conversation, ...prev];
			});
			setSelectedConversation(conversation);
			setIsNewChat(false);
			syncUrlWithSelection(conversation);
			setListRefreshToken((token) => token + 1);
		},
		[syncUrlWithSelection]
	);

	const handleConversationUpdated = useCallback((conversation: AiConversation) => {
		setConversations((prev) =>
			prev.map((item) =>
				item.conversationId === conversation.conversationId ? { ...item, ...conversation } : item
			)
		);
		setSelectedConversation((prev) =>
			prev?.conversationId === conversation.conversationId ? { ...prev, ...conversation } : prev
		);
	}, []);

	const handleConversationDeleted = useCallback(
		(conversationId: string) => {
			let clearedSelection = false;

			setConversations((prev) => prev.filter((item) => item.conversationId !== conversationId));
			setSelectedConversation((prev) => {
				if (prev?.conversationId !== conversationId) return prev;
				clearedSelection = true;
				return null;
			});

			if (clearedSelection) {
				setIsNewChat(false);
			}

			if (urlConversationId === conversationId) {
				syncUrlWithSelection(null);
			}
		},
		[syncUrlWithSelection, urlConversationId]
	);

	useEffect(() => {
		if (!urlConversationId) {
			urlLoadRequestIdRef.current += 1;
			urlConversationInFlightRef.current = '';
			urlConversationFailedRef.current = '';
			return;
		}
		if (selectedConversation?.conversationId === urlConversationId) return;

		const found = conversationsRef.current.find((item) => item.conversationId === urlConversationId);
		if (found) {
			setSelectedConversation(found);
			setIsNewChat(false);
			urlConversationFailedRef.current = '';
			return;
		}

		if (!canGet || urlConversationInFlightRef.current === urlConversationId) return;
		if (urlConversationFailedRef.current === urlConversationId) return;

		const requestId = ++urlLoadRequestIdRef.current;
		const requestedConversationId = urlConversationId;
		urlConversationInFlightRef.current = requestedConversationId;

		fetchConversation(requestedConversationId)
			.then((detail) => {
				if (requestId !== urlLoadRequestIdRef.current) return;

				const { messages: _messages, ...conversation } = detail;
				urlConversationFailedRef.current = '';
				setConversations((prev) => {
					const exists = prev.some((item) => item.conversationId === conversation.conversationId);
					if (exists) {
						return prev.map((item) =>
							item.conversationId === conversation.conversationId ? { ...item, ...conversation } : item
						);
					}
					return [conversation, ...prev];
				});
				setSelectedConversation(conversation);
				setIsNewChat(false);
			})
			.catch(() => {
				if (requestId !== urlLoadRequestIdRef.current) return;
				urlConversationFailedRef.current = requestedConversationId;
				setSelectedConversation(null);
				setIsNewChat(false);
				syncUrlWithSelection(null);
				toast.error('Conversation not found.');
			})
			.finally(() => {
				if (urlConversationInFlightRef.current === requestedConversationId) {
					urlConversationInFlightRef.current = '';
				}
			});
	}, [canGet, selectedConversation?.conversationId, syncUrlWithSelection, urlConversationId]);

	useEffect(() => {
		if (!urlConversationId) return;
		if (selectedConversation?.conversationId === urlConversationId) return;

		const found = conversations.find((item) => item.conversationId === urlConversationId);
		if (!found) return;

		setSelectedConversation(found);
		setIsNewChat(false);
		urlConversationFailedRef.current = '';
	}, [conversations, selectedConversation?.conversationId, urlConversationId]);

	useEffect(() => {
		if (!selectedConversation && !isNewChat && conversations.length > 0 && !urlConversationId) {
			handleSelectConversation(conversations[0]);
		}
	}, [conversations, handleSelectConversation, isNewChat, selectedConversation, urlConversationId]);

	return (
		<>
			<Header title="AI Assistant" data={firstLevelBreadcrumbs} />
			<div className="row mt-3">
				<div className="col-lg-12">
					<div className="telegram-conversations ai-assistant">
						<div className="telegram-conversations__layout">
							<ConversationListPanel
								conversations={conversations}
								setConversations={setConversations}
								selectedConversationId={selectedConversation?.conversationId ?? null}
								onSelectConversation={handleSelectConversation}
								onConversationDeleted={handleConversationDeleted}
								onConversationUpdated={handleConversationUpdated}
								refreshToken={listRefreshToken}
							/>
							<ChatPanel
								selectedConversation={selectedConversation}
								isNewChat={isNewChat}
								targetMessageId={targetMessageId}
								onConversationCreated={handleConversationCreated}
								onConversationUpdated={handleConversationUpdated}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default AiAssistant;
